import Constants from '@/Constants';
import { FileUploadData, useFileUpload } from '@/hooks/upload/FileUpload';
import { ApplicationState } from '@/store';
import React, { SyntheticEvent, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { TypeAccepted } from '..';
import ProgressUpload from '../Progress';

interface Props {
    typeAccepted: TypeAccepted;
    disabled: boolean;
    isLogo?: boolean;
    isBanner?: boolean;
    fileNameLimitSize?: number;
    onStart?: () => void;
    onComplete?: (response: FileUploadData) => void;
    onRemove?: () => void;
    onCancel?: () => void;
    isFullImageTypeAccepted?: boolean;
}

const SelectFileUpload: React.FC<Props> = (props: Props) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const { typeAccepted, disabled, fileNameLimitSize, isLogo, isBanner, onStart, onComplete, onCancel, onRemove, isFullImageTypeAccepted } = props;

    const { data: strings } = useSelector((state: ApplicationState) => state.locale);

    const [hightlight, setHightlight] = useState<boolean>(false);

    const { acceptConfiguration, uploadProcess, selectFileHandler, processFileHandler, cancelHandler } = useFileUpload({
        isLogo,
        isBanner,
        typeAccepted,
        fileNameLimitSize,
        inputRef,
        isFullImageTypeAccepted,
    });

    const disabledClass: string = disabled ? 'disabled' : '';

    const { accept, acceptLabel } = acceptConfiguration;
    const { progress, uploading } = uploadProcess;

    const getHighlight = () => {
        if (hightlight) {
            return { color: '#808080' };
        }
        return {};
    };

    const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setHightlight(true);
    };

    const onDragLeave = () => {
        setHightlight(false);
    };

    const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setHightlight(false);

        const { files } = event.dataTransfer;

        if (files.length > 1) {
            return;
        }
        processFileHandler(files[0], onStart, onComplete);
    };

    return (
        <div className="select-file-container" onDragLeave={onDragLeave} onDragOver={onDragOver} onDrop={onDrop}>
            {!uploading && (
                <>
                    <i className="material-icons-outlined" style={getHighlight()}>
                        {Constants.icons.CLOUD_UPLOAD}
                    </i>
                    <div className="d-flex flex-column align-items-start ml-3">
                        <span>
                            {strings.DROP_FILE_HERE}
                            <label className={`upload-link ${disabledClass}`}>
                                <input
                                    accept={accept}
                                    disabled={disabled}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => selectFileHandler(event, onStart, onComplete)}
                                    ref={inputRef}
                                    type="file"
                                />
                                {strings.SEARCH_FILE}
                            </label>
                        </span>
                        <span className="accept-label-text">{acceptLabel}</span>
                    </div>
                </>
            )}

            {uploading && (
                <>
                    <i className="material-icons-outlined">{Constants.icons.CLOUD_UPLOAD}</i>
                    <ProgressUpload onCancelHandler={(event: SyntheticEvent) => cancelHandler(event, onCancel, onRemove)} progress={progress} />
                </>
            )}
        </div>
    );
};

export default SelectFileUpload;
